/**
 * Screen Wake Lock for browser web (not Tauri): keep the display awake when the player is in a
 * kiosk-like context (Fullscreen API or installed PWA with display-mode: fullscreen).
 * Re-acquires after visibility returns (the lock is released when the document is hidden).
 */

import { isTauriRuntime } from '@/helpers/resolvedPlayerAppVersion'

let sentinel: WakeLockSentinel | null = null

function isTauriWebView(): boolean {
  return isTauriRuntime()
}

function shouldKeepScreenAwake(): boolean {
  if (typeof document === 'undefined') { return false }
  if (document.fullscreenElement !== null) { return true }
  try {
    return window.matchMedia('(display-mode: fullscreen)').matches
  }
  catch {
    return false
  }
}

async function releaseWakeLock(): Promise<void> {
  if (!sentinel) { return }
  try {
    await sentinel.release()
  }
  catch {
    // ignore
  }
  sentinel = null
}

async function syncWakeLock(): Promise<void> {
  if (!shouldKeepScreenAwake()) {
    await releaseWakeLock()
    return
  }
  if (!('wakeLock' in navigator) || navigator.wakeLock == null) { return }
  if (sentinel != null && !sentinel.released) { return }
  try {
    sentinel = await navigator.wakeLock.request('screen')
    sentinel.addEventListener('release', () => {
      sentinel = null
    })
  }
  catch {
    // NotAllowedError, unsupported context, etc.
  }
}

/**
 * Start listeners once. No-op outside a secure browser context or inside Tauri.
 */
export function startWebKioskWakeLock(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') { return }
  if (isTauriWebView()) { return }

  const onFullscreenChange = () => {
    void syncWakeLock()
  }
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void syncWakeLock()
    }
  }
  const onPageHide = () => {
    void releaseWakeLock()
  }

  document.addEventListener('fullscreenchange', onFullscreenChange)
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('pagehide', onPageHide)

  void syncWakeLock()
}
